// @ts-nocheck
import { useState, useCallback, useMemo } from 'react';
import { useValidation, FieldValidation, ValidationResult } from '../components/shared/ValidationSystem';
import { validators, formatters } from '../utils/sharedUtilities';

// Common validation rule builders
export const createCommonRules = {
  currency: (fieldName: string, min: number = 0, max: number = 10000000): FieldValidation => ({
    fieldName,
    rules: [
      {
        type: 'required',
        message: `${fieldName} is required`,
        severity: 'error'
      },
      {
        type: 'min',
        value: min,
        message: `${fieldName} must be at least ${formatters.currency(min)}`,
        severity: 'error'
      },
      {
        type: 'max',
        value: max,
        message: `${fieldName} cannot exceed ${formatters.currency(max)}`,
        severity: 'warning'
      }
    ],
    guidance: `Enter the ${fieldName.toLowerCase()} in dollars`,
    example: formatters.currency(Math.min(max / 10, 50000))
  }),

  percentage: (fieldName: string, min: number = 0, max: number = 100): FieldValidation => ({
    fieldName,
    rules: [
      {
        type: 'required',
        message: `${fieldName} is required`,
        severity: 'error'
      },
      {
        type: 'range',
        min,
        max,
        message: `${fieldName} must be between ${min}% and ${max}%`,
        severity: 'error'
      }
    ],
    guidance: `Enter the ${fieldName.toLowerCase()} as a percentage`,
    example: `${Math.round((min + max) / 2)}%`
  }),

  years: (fieldName: string, min: number = 1, max: number = 50): FieldValidation => ({
    fieldName,
    rules: [
      {
        type: 'required',
        message: `${fieldName} is required`,
        severity: 'error'
      },
      {
        type: 'range',
        min,
        max,
        message: `${fieldName} must be between ${min} and ${max} years`,
        severity: 'error'
      }
    ],
    guidance: `Enter the ${fieldName.toLowerCase()} in years`,
    example: `${Math.round((min + max) / 2)} years`
  }),

  rate: (fieldName: string, type: 'mortgage' | 'investment' | 'inflation'): FieldValidation => {
    const ranges = {
      mortgage: { min: 1, max: 15, typical: [3, 8], example: 5.25 },
      investment: { min: 0, max: 20, typical: [4, 12], example: 7.5 },
      inflation: { min: 0, max: 10, typical: [1, 5], example: 2.5 }
    };
    
    const range = ranges[type];
    
    return {
      fieldName,
      rules: [
        {
          type: 'required',
          message: `${fieldName} is required`,
          severity: 'error'
        },
        {
          type: 'range',
          min: range.min,
          max: range.max,
          message: `${fieldName} must be between ${range.min}% and ${range.max}%`,
          severity: 'error'
        },
        {
          type: 'custom',
          validator: (value) => value >= range.typical[0] && value <= range.typical[1],
          message: `This ${type} rate seems unusual - verify current market rates`,
          severity: 'warning'
        }
      ],
      guidance: `Enter the ${fieldName.toLowerCase()} as an annual percentage`,
      example: `${range.example}%`
    };
  },

  downPayment: (fieldName: string, housePriceField: string): FieldValidation => ({
    fieldName,
    rules: [
      {
        type: 'required',
        message: `${fieldName} is required`,
        severity: 'error'
      },
      {
        type: 'min',
        value: 5000,
        message: `${fieldName} must be at least $5,000`,
        severity: 'error'
      },
      {
        type: 'custom',
        validator: (value, allData) => {
          if (!allData || !allData[housePriceField]) return true;
          const percentage = (value / allData[housePriceField]) * 100;
          return percentage >= 5;
        },
        message: 'Down payment must be at least 5% of house price',
        severity: 'error'
      },
      {
        type: 'custom',
        validator: (value, allData) => {
          if (!allData || !allData[housePriceField]) return true;
          const percentage = (value / allData[housePriceField]) * 100;
          return percentage >= 10;
        },
        message: 'Down payment below 10% may require mortgage insurance',
        severity: 'warning'
      }
    ],
    guidance: 'Typical down payment is 5-20% of home price. Higher down payment reduces monthly payments.',
    example: '$90,000 (20% of $450,000)'
  })
};

// Shared validation patterns for different calculator types
export const validationPatterns = {
  housing: {
    houseCost: createCommonRules.currency('House Cost', 50000, 5000000),
    downpaymentAmount: createCommonRules.downPayment('Down Payment Amount', 'houseCost'),
    downpaymentPercent: createCommonRules.percentage('Down Payment Percentage', 5, 50),
    interestRate: createCommonRules.rate('Mortgage Interest Rate', 'mortgage'),
    mortgageYears: createCommonRules.years('Mortgage Term', 5, 30),
    ownershipYears: createCommonRules.years('Ownership Period', 1, 50),
    homeAppreciationRate: createCommonRules.rate('Home Appreciation Rate', 'investment'),
    propertyTaxPercent: createCommonRules.percentage('Property Tax Rate', 0, 5),
    maintenancePercent: createCommonRules.percentage('Maintenance Rate', 0, 5),
    monthlyRent: createCommonRules.currency('Monthly Rent', 200, 10000),
    investmentReturnRate: createCommonRules.rate('Investment Return Rate', 'investment'),
    closingCostsPercent: createCommonRules.percentage('Closing Costs', 0.5, 8),
    sellingCostsPercent: createCommonRules.percentage('Selling Costs', 1, 12),
    condoFees: createCommonRules.currency('Condo Fees', 0, 2000),
    rentalInflationRate: createCommonRules.rate('Rental Inflation Rate', 'inflation'),
    taxRate: createCommonRules.percentage('Tax Rate', 0, 60)
  },

  car: {
    totalCost: createCommonRules.currency('Total Cost', 5000, 200000),
    downPayment: createCommonRules.currency('Down Payment', 0, 100000),
    financeRate: createCommonRules.rate('Finance Rate', 'mortgage'),
    financeYears: createCommonRules.years('Finance Term', 1, 8),
    leasePayment: createCommonRules.currency('Lease Payment', 100, 2000),
    leaseAPR: createCommonRules.rate('Lease APR', 'mortgage'),
    leaseTerm: createCommonRules.years('Lease Term', 1, 5),
    monthlyInsurance: createCommonRules.currency('Monthly Insurance', 50, 1000),
    monthlyFuel: createCommonRules.currency('Monthly Fuel', 50, 800),
    monthlyMaintenance: createCommonRules.currency('Monthly Maintenance', 20, 500),
    inflationRate: createCommonRules.rate('Inflation Rate', 'inflation'),
    investmentReturn: createCommonRules.rate('Investment Return', 'investment'),
    timeHorizon: createCommonRules.years('Time Horizon', 1, 20)
  },

  retirement: {
    currentAge: createCommonRules.years('Current Age', 18, 90),
    retirementAge: createCommonRules.years('Retirement Age', 50, 90),
    lifeExpectancy: createCommonRules.years('Life Expectancy', 60, 120),
    currentIncome: createCommonRules.currency('Current Income', 20000, 500000),
    annualSpending: createCommonRules.currency('Annual Spending', 10000, 300000),
    rrspBalance: createCommonRules.currency('RRSP Balance', 0, 2000000),
    tfsaBalance: createCommonRules.currency('TFSA Balance', 0, 500000),
    nonRegisteredBalance: createCommonRules.currency('Non-Registered Balance', 0, 5000000),
    expectedReturn: createCommonRules.rate('Expected Return', 'investment'),
    inflationRate: createCommonRules.rate('Inflation Rate', 'inflation'),
    marginalTaxRate: createCommonRules.percentage('Marginal Tax Rate', 15, 60)
  }
};

// Hook for using common validation patterns
export const useSharedValidation = (
  calculatorType: keyof typeof validationPatterns,
  customRules: FieldValidation[] = []
) => {
  const baseRules = validationPatterns[calculatorType];
  const allRules = useMemo(() => {
    const baseRulesList = Object.values(baseRules);
    return [...baseRulesList, ...customRules];
  }, [baseRules, customRules]);

  const validation = useValidation(allRules);

  // Enhanced validation with common patterns
  const validateWithPattern = useCallback((
    fieldName: string, 
    value: any, 
    allData?: any
  ) => {
    const result = validation.validateField(fieldName, value, allData);
    
    // Add common cross-field validations
    if (calculatorType === 'housing' && allData) {
      if (fieldName === 'downpaymentAmount' && allData.houseCost) {
        const percentage = (value / allData.houseCost) * 100;
        if (percentage > 50) {
          result.warnings.push({
            field: fieldName,
            message: 'Very high down payment - consider opportunity cost of this much cash',
            severity: 'warning',
            rule: 'custom'
          });
        }
      }
      
      if (fieldName === 'ownershipYears' && value < 5) {
        result.warnings.push({
          field: fieldName,
          message: 'Short ownership period may not cover transaction costs',
          severity: 'warning',
          rule: 'custom'
        });
      }
    }

    if (calculatorType === 'retirement' && allData) {
      if (fieldName === 'retirementAge' && allData.currentAge && value <= allData.currentAge) {
        result.errors.push({
          field: fieldName,
          message: 'Retirement age must be greater than current age',
          severity: 'error',
          rule: 'custom'
        });
        result.isValid = false;
      }
    }

    return result;
  }, [validation, calculatorType]);

  return {
    ...validation,
    validateWithPattern,
    patterns: baseRules
  };
};

// Hook for managing form state with validation
export const useValidatedForm = <T extends Record<string, any>>(
  initialData: T,
  calculatorType: keyof typeof validationPatterns,
  customRules: FieldValidation[] = []
) => {
  const [data, setData] = useState<T>(initialData);
  const validation = useSharedValidation(calculatorType, customRules);

  const updateField = useCallback((
    field: keyof T, 
    value: any, 
    validateImmediately: boolean = false
  ) => {
    setData(prev => {
      const newData = { ...prev, [field]: value };
      
      if (validateImmediately) {
        // Trigger validation after state update
        setTimeout(() => {
          validation.validateWithPattern(field as string, value, newData);
        }, 0);
      }
      
      return newData;
    });
  }, [validation]);

  const validateAll = useCallback(() => {
    return validation.validateAll(data);
  }, [validation, data]);

  const isFormValid = useMemo(() => {
    return !validation.hasErrors;
  }, [validation.hasErrors]);

  return {
    data,
    updateField,
    validation,
    validateAll,
    isFormValid,
    setData
  };
};

export default useSharedValidation;